import React from 'react';
import { useSelectFilter } from '../hooks/useSelectFilter';
import { useFloatingFilterMenu } from '../hooks/useFloatingFilterMenu';
import { BaseFilter, BaseFilterProps } from '../components/BaseFilter';
import { BareSvg } from '@/lib/bareSvg';
import { SvgIcon } from '@/types';
import styles from '../StanFilters.module.css';
import dropdownStyles from '../../StanDropdown/Menu/StanDropdownMenu.module.css';
import { cn } from '@/lib/utils/cn';

type Props = BaseFilterProps;

/**
 * Select filter component for filtering with predefined options
 * Supports custom dropdown options with icons and labels
 */
const SelectFilter: React.FC<Props> = ({ id, label, filterItem, onApply, onClearFilter, disabled, placement }) => {
    const { menuRef, triggerProps, isOpen, handleOpenChange, closeMenu } = useFloatingFilterMenu(disabled);

    const {
        summary,
        hasSummary,
        dropdownOptions,
        currentValue,
        clearFilters,
        // Extracted complex logic from hook
        handleOptionClick,
        getIconConfig,
    } = useSelectFilter({
        filterItem,
        onApply,
        onClearFilter,
        disabled,
        closeMenu,
    });

    /**
     * Renders an icon based on its configuration
     */
    const renderIcon = (icon: string | SvgIcon, label: string) => {
        const iconConfig = getIconConfig(icon, label);

        if (iconConfig.type === 'img') {
            return (
                <img src={iconConfig.src} alt={iconConfig.alt} width={iconConfig.width} height={iconConfig.height} />
            );
        } else {
            return (
                <BareSvg
                    SvgComponent={iconConfig.component}
                    removeFill={iconConfig.removeFill}
                    width={iconConfig.width}
                    height={iconConfig.height}
                />
            );
        }
    };

    return (
        <BaseFilter
            id={id}
            label={label}
            filterItem={filterItem}
            onApply={onApply}
            onClearFilter={onClearFilter}
            disabled={disabled}
            placement={placement}
            menuRef={menuRef}
            triggerProps={triggerProps}
            summary={summary}
            hasSummary={hasSummary}
            isOpen={isOpen}
            handleOpenChange={handleOpenChange}
            clearFilters={clearFilters}
            menuClassName={styles.selectFilterMenu}
        >
            <div className={dropdownStyles.list}>
                {dropdownOptions.map(option => {
                    const isActive = currentValue?.value === option.value;
                    return (
                        <button
                            key={option.value}
                            type='button'
                            className={cn(
                                dropdownStyles.item,
                                isActive ? dropdownStyles.selected : '',
                                option.disabled ? dropdownStyles.disabled : ''
                            )}
                            onClick={() => handleOptionClick(option.value)}
                            disabled={option.disabled}
                        >
                            <div className={dropdownStyles.itemContent}>
                                {option.icon && (
                                    <div className={dropdownStyles.iconWrapper}>
                                        {renderIcon(option.icon, option.label)}
                                    </div>
                                )}
                                <span className={dropdownStyles.itemLabel}>{option.label}</span>
                            </div>
                        </button>
                    );
                })}
            </div>
        </BaseFilter>
    );
};

export default SelectFilter;
